import { differenceInDays, isFuture, parseISO } from 'date-fns'

import constants from '@/constants'
import api from '@/logic/api'
import DateManager from '@/logic/DateManager'

export default {
  allAppointments(state, getters, rootState, rootGetters) {
    if (!state.appointments) {
      return null
    }
    const appointments = Object.values(state.appointments)
      .map((appointment) => {
        if (appointment.participation_management_enabled && !appointment.is_cancelled) {
          const registrationDeadlineHasPassed = appointment.registration_deadline_expires_at
            && parseISO(appointment.registration_deadline_expires_at).getTime() < state.deadlineNow
          if (appointment.user_participates) {
            appointment.badge = api.i18n.t('appointments.youAreRegistered')
            appointment.badgeType = 'success'
          } else if (isFuture(DateManager.parseModelDate(appointment.end_date)) && !registrationDeadlineHasPassed) {
            if (!appointment.limit_participants) {
              appointment.badge = api.i18n.t('appointments.openSlots')
              appointment.badgeType = 'neutral'
            } else if (appointment.participants_count >= appointment.max_participants) {
              appointment.badge = api.i18n.t('appointments.noOpenSlots')
            } else {
              const openSlots = appointment.max_participants - appointment.participants_count
              appointment.badge = api.i18n.tc('appointments.nOpenSlots', openSlots, { openSlots })
              appointment.badgeType = 'neutral'
            }
          } else if (registrationDeadlineHasPassed) {
            appointment.badge = null
            appointment.badgeType = null
          }
        }
        return appointment
      })
    // we're simulating relevant parts of the appointment model object here:
    // id: prefix with foreign model
    // cover_image_url: image of foreign model if available
    // created_at: unused
    // description: unused
    // end_date: null or calculated from foreign model
    // is_cancelled: false
    // location: unused
    // name: get from title
    // start_date: calculated from foreign model
    // type: depending on foreign model
    // updated_at: unused
    //
    // Additional props:
    // route: route parameters for resource
    // important: true if entry should be emphasized
    // resolved: true if the user already took care of it
    // badge: badge text to show next to date
    const competitionAppointments = (rootGetters['competitions/competitions'] || [])
      .map((competition) => ({
        id: `competition-${competition.id}`,
        cover_image_url: competition.cover_image_url,
        start_date: competition.end, // deadline
        is_cancelled: false,
        name: competition.title,
        route: {
          name: 'competitions.details',
          params: {
            id: competition.id,
          },
        },
        type: 'COMPETITION',
      }))

    const testAppointments = (rootGetters['tests/tests'] || [])
      .filter((test) => !!test.active_until)
      .map((test) => {
        const activeUntil = DateManager.parseModelDate(test.active_until)
        return {
          id: `test-${test.id}`,
          cover_image_url: test.cover_image_url,
          start_date: test.active_until, // deadline
          is_cancelled: false,
          name: test.name,
          route: {
            name: 'tests.details',
            params: {
              testId: test.id,
            },
          },
          important: isFuture(activeUntil) && differenceInDays(activeUntil, new Date()) < 7,
          type: 'TEST',
        }
      })
    const webinarAppointments = (rootGetters['webinars/webinars'] || [])
      .filter((webinar) => ((
        (
          webinar.type === constants.WEBINARS.TYPE_WEBINAR
        )
      )))
      .map((webinar) => ({
        id: `webinar-${webinar.id}`,
        start_date: webinar.starts_at, // deadline
        is_cancelled: false,
        name: webinar.topic,
        route: {
          name: 'webinars.details',
          params: {
            id: webinar.id,
          },
        },
        type: 'WEBINAR',
      }))
    return appointments
      .concat(competitionAppointments)
      .concat(testAppointments)
      .concat(webinarAppointments)
  },
  upcomingAppointments(state, getters) {
    if (!state.appointments) {
      return null
    }
    return getters.allAppointments
      .filter((appointment) => {
        return isFuture(DateManager.parseModelDate(appointment.end_date || appointment.start_date))
      })
      .sort((appointmentA, appointmentB) => {
        if (appointmentA.start_date > appointmentB.start_date) {
          return 1
        }
        if (appointmentA.start_date < appointmentB.start_date) {
          return -1
        }
        return 0
      })
  },
  upcomingAppointmentsWithinNDays: (state, getters) => (days = 30) => {
    if (!state.appointments) {
      return null
    }
    const now = new Date()
    return getters.allAppointments
      .filter((appointment) => {
        const startDate = DateManager.parseModelDate(appointment.start_date)
        const endDate = DateManager.parseModelDate(appointment.end_date || appointment.start_date)
        const daysUntilStart = differenceInDays(startDate, now)
        const hasStarted = startDate <= now
        const inWindow = daysUntilStart >= 0 && daysUntilStart <= days
        return isFuture(endDate) && (hasStarted || inWindow)
      })
      .sort((a, b) => {
        if (a.start_date > b.start_date) return 1
        if (a.start_date < b.start_date) return -1
        return 0
      })
  },
  getAppointment(state) {
    if (!state.appointments) {
      return () => null
    }
    return (id) => state.appointments[id]
  },
  searchQuery(state) {
    return state.searchQuery
  },
  filter(state) {
    return state.filter
  },
  sort(state) {
    return state.sort
  },
  activeCategoryFilters(state) {
    return state.activeCategoryFilters
  },
  searchOpen(state) {
    return state.searchOpen
  },
  filteredAppointments(state, getters) {
    if (!getters.allAppointments) {
      return null
    }

    let filtered = getters.allAppointments

    // Apply search filter
    if (state.searchQuery.trim()) {
      const query = state.searchQuery.toLowerCase()
      filtered = filtered.filter((appointment) => {
        return appointment.name?.toLowerCase().includes(query)
          || appointment.description?.toLowerCase().includes(query)
          || appointment.id?.toString().includes(query)
      })
    }

    // Apply filter tabs
    if (state.filter === constants.APPOINTMENTS.FILTER_UPCOMING) {
      filtered = filtered.filter((appointment) => {
        return isFuture(DateManager.parseModelDate(appointment.end_date || appointment.start_date))
      })
    } else if (state.filter === constants.APPOINTMENTS.FILTER_PARTICIPATING) {
      filtered = filtered.filter((appointment) => appointment.user_participates)
    } else if (state.filter === constants.APPOINTMENTS.FILTER_ONLINE) {
      filtered = filtered.filter((appointment) => appointment.type === constants.APPOINTMENTS.TYPE_ONLINE)
    } else if (state.filter === constants.APPOINTMENTS.FILTER_IN_PERSON) {
      filtered = filtered.filter((appointment) => appointment.type === constants.APPOINTMENTS.TYPE_IN_PERSON)
    } else if (state.filter === constants.APPOINTMENTS.FILTER_PAST) {
      filtered = filtered.filter((appointment) => {
        return !isFuture(DateManager.parseModelDate(appointment.end_date || appointment.start_date))
      })
    }

    // Apply category filters
    if (state.activeCategoryFilters.length > 0) {
      filtered = filtered.filter((appointment) => {
        return appointment.categories?.some((category) => state.activeCategoryFilters.includes(category.id))
      })
    }
    return filtered
  },
  sortedAppointments(state, getters) {
    if (!getters.filteredAppointments) {
      return null
    }

    const sorted = [...getters.filteredAppointments]

    if (state.sort === constants.APPOINTMENTS.SORT_RELEVANT) {
      const now = new Date()

      // Determine behaviour based on active filter
      if (state.filter === constants.APPOINTMENTS.FILTER_PAST) {
        // Only past appointments – latest first
        sorted.sort((a, b) => {
          const dateA = DateManager.parseModelDate(a.end_date || a.start_date)
          const dateB = DateManager.parseModelDate(b.end_date || b.start_date)
          return dateB - dateA
        })
      } else if (state.filter === constants.APPOINTMENTS.FILTER_UPCOMING) {
        // Only upcoming – soonest first
        sorted.sort((a, b) => {
          const dateA = DateManager.parseModelDate(a.end_date || a.start_date)
          const dateB = DateManager.parseModelDate(b.end_date || b.start_date)
          return dateA - dateB
        })
      } else {
        // Mixed list (participating, online, in-person)
        sorted.sort((a, b) => {
          const dateA = DateManager.parseModelDate(a.end_date || a.start_date)
          const dateB = DateManager.parseModelDate(b.end_date || b.start_date)
          const aIsFuture = dateA > now
          const bIsFuture = dateB > now

          if (aIsFuture && !bIsFuture) return -1
          if (!aIsFuture && bIsFuture) return 1

          // Both upcoming
          if (aIsFuture && bIsFuture) return dateA - dateB
          // Both past
          return dateB - dateA
        })
      }
    } else if (state.sort === constants.APPOINTMENTS.SORT_START_DATE_ASC) {
      sorted.sort((a, b) => {
        const dateA = DateManager.parseModelDate(a.start_date)
        const dateB = DateManager.parseModelDate(b.start_date)
        return dateA - dateB
      })
    } else if (state.sort === constants.APPOINTMENTS.SORT_START_DATE_DESC) {
      sorted.sort((a, b) => {
        const dateA = DateManager.parseModelDate(a.start_date)
        const dateB = DateManager.parseModelDate(b.start_date)
        return dateB - dateA
      })
    } else if (state.sort === constants.APPOINTMENTS.SORT_NAME_ASC) {
      sorted.sort((a, b) => a.name.localeCompare(b.name))
    } else if (state.sort === constants.APPOINTMENTS.SORT_NAME_DESC) {
      sorted.sort((a, b) => b.name.localeCompare(a.name))
    }

    return sorted
  },
  visibleAppointments(state, getters) {
    return getters.sortedAppointments
  },
  availableCategories(state, getters) {
    if (!getters.allAppointments) {
      return null
    }

    const categoriesMap = new Map()
    getters.allAppointments.forEach((appointment) => {
      if (appointment.categories) {
        appointment.categories.forEach((category) => {
          if (!categoriesMap.has(category.id)) {
            categoriesMap.set(category.id, category)
          }
        })
      }
    })

    return Array.from(categoriesMap.values())
  },
  appointmentsFiltersCounts(state, getters) {
    if (!getters.allAppointments) {
      return {
        upcoming: 0,
        participating: 0,
        online: 0,
        inPerson: 0,
        past: 0,
      }
    }

    const counts = {
      upcoming: 0,
      participating: 0,
      online: 0,
      inPerson: 0,
      past: 0,
    }

    let filtered = getters.allAppointments

    // Apply category filters
    if (state.activeCategoryFilters.length > 0) {
      filtered = filtered.filter((appointment) => {
        return appointment.categories?.some((category) => state.activeCategoryFilters.includes(category.id))
      })
    }

    filtered.forEach((appointment) => {
      // Upcoming
      if (appointment.type !== 'COURSE' || !appointment.resolved) {
        if (isFuture(DateManager.parseModelDate(appointment.end_date || appointment.start_date))) {
          counts.upcoming++
        }
      }

      // Participating
      if (appointment.user_participates) {
        counts.participating++
      }

      // Online
      if (appointment.type === constants.APPOINTMENTS.TYPE_ONLINE) {
        counts.online++
      }

      // In-person
      if (appointment.type === constants.APPOINTMENTS.TYPE_IN_PERSON) {
        counts.inPerson++
      }

      // Past
      if (appointment.type === 'COURSE' && appointment.resolved) {
        counts.past++
      } else if (!isFuture(DateManager.parseModelDate(appointment.end_date || appointment.start_date))) {
        counts.past++
      }
    })

    return counts
  },
}
